import { ProFormSelect } from '@ant-design/pro-form';
import { Tooltip, SelectProps } from 'antd';
import { observer } from 'mobx-react-lite';
import React, {
  FC,
  Fragment,
  useState,
} from 'react';
import {
  SelectGroupFormTypeField,
  SelectGroupFormTypeOption,
} from '@/presentation/components/form/index';
import {
  getValueFromEvent,
  getValueFromEventOption,
  useOptions,
} from '@/presentation/components/form/utils';
import { ExOption, Option } from '@/proto/field';
import styles from './select-group.module.scss';

export enum GroupName {
  tags = 'tags',
  tagFilter = 'tagFilter',
  problemType = 'problemTypeCompliance',
  problemTypeFilter = 'problemTypeComplianceFilter',
  privacyFunction = 'privacyFunctionCompliance',
  privacyFunctionFilter = 'privacyFunctionComplianceFilter',
  coveredStatus = 'coveredStatusCompliance',
  coveredStatusFilter = 'coveredStatusComplianceFilter',
  fuzzyMatch = 'fuzzyMatchCompliance',
  fuzzyMatchFilter = 'fuzzyMatchComplianceFilter',
  hostType = 'hostTypeCompliance',
  hostTypeFilter = 'hostTypeComplianceFilter',
}

interface IProps {
  label: string | React.ReactNode;
  leftName: string;
  rightName: string;
  leftOptions: Option[] | ExOption[];
  rightOptions: Option[] | ExOption[];
  mode: 'multiple' | 'tags' | 'single';
  leftInitialValue?: any;
  rightInitialValue?: any;
  rightProps?: SelectProps;
}

const SelectGroup: FC<IProps> = (props) => {
  const { rightProps } = props;
  const [isFocus, setIsFocus] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [value, setValue] = useState<Option[] | ExOption[]>([]);

  const onChange = (_args: string[], option: any) => {
    const value = getValueFromEventOption(option as Option[] | ExOption[], props.mode);
    setValue(value);
  };

  const renderTips = () => <>{Array.isArray(value) ? value.map(item => <div>{item.label}</div>) : null}</>;

  const tipsVisible = isHover && !isFocus && !dropdownVisible && Array.isArray(value) && value.length > 1;

  return <Fragment>
    <div className="ant-form-item-label">
      {props.label}
    </div>
    <div className={styles.selectGroup}>
      <span data-tid={props.leftName} className={styles.selectLeft}>
        <ProFormSelect
          name={props.leftName}
          options={props.leftOptions.map(item => ({
            ...item,
            ['data-tid']: `${props.leftName}-${item.label}`,
          }))}
          initialValue={props.leftInitialValue}
          allowClear={false}
          fieldProps={{
            dropdownClassName: 'reset-font-size',
          }}
        />
      </span>
      <span data-tid={props.rightName} className={styles.selectRight}>
        <Tooltip title={renderTips()} visible={tipsVisible}>
          <ProFormSelect
            name={props.rightName}
            options={props.rightOptions.map(item => ({
              ...item,
              ['data-tid']: `${props.rightName}-${item.label}`,
            }))}
            mode={props.mode}
            initialValue={props.rightInitialValue}
            getValueFromEvent={(args: string[]) => getValueFromEvent(args, props.mode!)
            }
            fieldProps={{
              showSearch: true,
              maxTagCount: 'responsive',
              dropdownClassName: 'reset-font-size',
              onFocus: () => setIsFocus(true),
              onBlur: () => setIsFocus(false),
              onMouseEnter: () => setIsHover(true),
              onMouseLeave: () => setIsHover(false),
              onDropdownVisibleChange: open => setDropdownVisible(open),
              onChange,
              ...rightProps,
            }}
          />
        </Tooltip>
      </span>
    </div>
  </Fragment>;
};

export const SelectGroupForm: FC<{
  entry: SelectGroupFormTypeField | SelectGroupFormTypeOption;
  hidden?: boolean;
}> = observer((props) => {
  const { entry, hidden } = props;
  const {
    leftName,
    rightName,
    label,
    mode,
    leftInitialValue,
    rightInitialValue,
    rightProps,
  } = entry;

  if (hidden) {
    return null;
  }

  const leftFieldKey = 'leftFieldKey' in entry ? (entry as SelectGroupFormTypeField).leftFieldKey : undefined;
  const rightFieldKey = 'rightFieldKey' in entry ? (entry as SelectGroupFormTypeField).rightFieldKey : undefined;
  const fieldScope = 'fieldScope' in entry ? (entry as SelectGroupFormTypeField).fieldScope : undefined;
  const leftUserOptions = 'leftOptions' in entry ? (entry as SelectGroupFormTypeOption).leftOptions : undefined;
  const rightUserOptions = 'rightOptions' in entry ? (entry as SelectGroupFormTypeOption).rightOptions : undefined;
  const leftOptions = useOptions({
    fieldKey: leftFieldKey,
    fieldScope,
    userOptions: leftUserOptions,
  });
  const rightOptions = useOptions({
    fieldKey: rightFieldKey,
    fieldScope,
    userOptions: rightUserOptions,
  });

  return (
    <SelectGroup
      label={label}
      leftName={leftName}
      leftOptions={leftOptions}
      leftInitialValue={leftInitialValue}
      rightName={rightName}
      rightOptions={rightOptions}
      rightInitialValue={rightInitialValue}
      mode={mode}
      rightProps={rightProps}
    />
  );
});
